import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import Logo from './Logo';

const triggerAnimations = (): void => {
  console.log("Triggering forceAnimations event");
  window.dispatchEvent(new CustomEvent('forceAnimations', {
    detail: { timestamp: Date.now() }
  }));
};

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setVisible(false);
        if (isOpen) setIsOpen(false);
      } else {
        setVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY, isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const navItems = [
    { name: 'About', href: '/#about' },
    { name: 'Acquisition Paths', href: '/#specialty-structures' },
    { name: 'Focus Areas', href: '/#portfolio' },
    { name: 'Platforms', href: '/#platforms' },
    { name: 'Approach', href: '/#approach' },
    { name: 'Our Process', href: '/#team' },
    { name: 'Contact', href: '/#contact' },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: visible ? 0 : -100 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-md bg-white/90 shadow-lg py-2'
          : 'backdrop-blur-sm bg-white/10 py-4'
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 flex justify-between items-center relative z-10">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Logo scrolled={scrolled} />
        </motion.div>

        <nav className="hidden md:flex items-center space-x-8">
          {navItems.map((item, index) => (
            <motion.a
              key={item.name}
              href={item.href}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 * (index + 1) }}
              className={`text-sm font-medium transition-all duration-300 relative group ${
                scrolled ? 'text-gray-800' : 'text-gray-800'
              }`}
              onClick={(e) => {
                if (item.href.includes('#')) {
                  e.preventDefault();
                  const id = item.href.split('#')[1];

                  if (window.location.pathname !== '/') {
                    console.log(`Desktop nav: Not on homepage, navigating to /#${id}`);
                    sessionStorage.setItem('scrollToSection', id);
                    window.location.href = '/';
                    return;
                  } else {
                    const element = document.getElementById(id);
                    if (element) {
                      console.log(`Desktop nav: Found element #${id}, scrolling into view`);
                      element.scrollIntoView({ behavior: 'smooth' });
                      triggerAnimations();
                    } else {
                      console.error(`Desktop nav: Element #${id} not found`);
                    }
                  }
                }
              }}
            >
              {item.name}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-500 transition-all duration-300 group-hover:w-full"></span>
            </motion.a>
          ))}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.7 }}
            whileHover={{
              scale: 1.05,
              boxShadow: "0 10px 15px -3px rgba(59, 130, 246, 0.3)"
            }}
            whileTap={{ scale: 0.95 }}
            className="px-5 py-2 rounded-md bg-gradient-to-r from-primary-600 to-primary-500 text-white text-sm font-medium transition-all duration-300 hover:from-primary-700 hover:to-primary-600 shadow-md hover:shadow-lg"
          >
            <Link to="/get-started" className="flex items-center text-white">
              <span>Get Started</span>
              <ChevronDown size={16} className="ml-1 transition-transform group-hover:rotate-180" />
            </Link>
          </motion.div>
        </nav>

        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.5 }}
          className="md:hidden text-gray-800 focus:outline-none relative z-50"
          onClick={toggleMenu}
          aria-label="Toggle menu"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </motion.button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="md:hidden bg-white/95 backdrop-blur-md w-full overflow-hidden shadow-lg"
          >
            <div className="container mx-auto px-4 py-6 flex flex-col space-y-5">
              {navItems.map((item, index) => (
                <motion.a
                  key={item.name}
                  href={item.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.05 * index }}
                  className="text-gray-800 text-lg font-medium transition-all duration-300 hover:text-primary-600 hover:translate-x-2 flex items-center"
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault();
                    setIsOpen(false);
                    console.log(`Navigation clicked: ${item.name} -> ${item.href}`);

                    if (item.href.includes('#')) {
                      const id = item.href.split('#')[1];
                      console.log(`Navigating to section: #${id}`);

                      setTimeout(() => {
                        if (window.location.pathname !== '/') {
                          console.log(`Mobile nav: Not on homepage, navigating to /#${id}`);
                          sessionStorage.setItem('scrollToSection', id);
                          window.location.href = '/';
                          return;
                        } else {
                          const element = document.getElementById(id);
                          if (element) {
                            console.log(`Mobile nav: Found element #${id}, scrolling into view`);
                            element.scrollIntoView({ behavior: 'smooth' });
                            triggerAnimations();
                          } else {
                            console.error(`Mobile nav: Element #${id} not found`);
                          }
                        }
                      }, 100);
                    } else {
                      window.location.href = item.href;
                    }
                  }}
                >
                  {item.name}
                </motion.a>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="mt-4 px-5 py-3 rounded-md bg-gradient-to-r from-primary-600 to-primary-500 text-white text-lg font-medium transition-all duration-300 hover:from-primary-700 hover:to-primary-600 shadow-md hover:shadow-lg text-center"
              >
                <Link
                  to="/get-started"
                  className="text-white block w-full h-full"
                  onClick={() => {
                    setIsOpen(false);
                    console.log("Get Started clicked, navigating to /get-started");
                  }}
                >
                  Get Started
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
